import React from 'react';
import { Kpi, Card, Line, Donut, Heat, HBars, Meter, ICN } from './Charts';

// Reports / Quality tab — LQA + MQM error analytics.

const SEV = [
    { name: 'Minor', color: 'var(--ink-4)' },
    { name: 'Major', color: 'var(--yellow)' },
    { name: 'Critical', color: '#c8553a' },
];
const LQA_STATUS = {
    pass: { cls: 'ok', label: 'Pass' },
    rework: { cls: 'warn', label: 'Rework' },
    fail: { cls: 'bad', label: 'Fail' },
};

const QualityTab = ({ D, filters }) => {
    const Q = D.QUALITY_KPI;
    const sev = D.MQM.reduce((a, r) => { a.minor += r[1]; a.major += r[2]; a.crit += r[3]; return a; }, { minor: 0, major: 0, crit: 0 });
    const sevTotal = sev.minor + sev.major + sev.crit;
    const langFilter = (filters && filters.lang) || [];
    let langs = D.LANGS.slice(0, 6);
    if (langFilter.length) langs = D.LANGS.filter((l) => langFilter.includes(l[1]));

    return (
        <>
            <div className="rpt-kpis">
                <Kpi label="LQA score" value={Q.lqa} delta={Q.lqaDelta} arrow="up" tone="good" spark={D.QUALITY_TREND} color="var(--indigo)" />
                <Kpi label="MQM error rate" unit="/1k" value={Q.mqm} delta={Q.mqmDelta} arrow="down" tone="good" color="var(--accent)" />
                <Kpi label="First-pass rate" value={Q.pass} delta={Q.passDelta} arrow="up" tone="good" color="var(--green)" />
                <Kpi label="Rework rate" value={Q.rework} delta={Q.reworkDelta} arrow="down" tone="good" color="var(--yellow)" />
            </div>

            <div className="rpt-row a">
                <Card title="Quality trend" sub="avg LQA score / week">
                    <div className="rpt-plot">
                        <Line data={D.QUALITY_TREND} w={720} h={240} min={93.5} max={97} ticks={[94, 95, 96, 97]} target={95} targetLabel="quality gate 95" color="var(--indigo)" />
                    </div>
                </Card>
                <Card title="Error severity" sub={`${sevTotal} issues`}>
                    <div className="rpt-donut-wrap col">
                        <Donut segments={[
                            { value: sevTotal ? Math.round((sev.minor / sevTotal) * 100) : 0, color: 'var(--ink-4)', label: 'Minor' },
                            { value: sevTotal ? Math.round((sev.major / sevTotal) * 100) : 0, color: 'var(--yellow)', label: 'Major' },
                            { value: sevTotal ? Math.round((sev.crit / sevTotal) * 100) : 0, color: '#c8553a', label: 'Critical' },
                        ]} center={sevTotal} sub="issues" />
                        <div className="rpt-cost-legend">
                            <div><span className="dot" style={{ background: 'var(--ink-4)' }} />Minor<b>{sev.minor}</b></div>
                            <div><span className="dot" style={{ background: 'var(--yellow)' }} />Major<b>{sev.major}</b></div>
                            <div><span className="dot" style={{ background: '#c8553a' }} />Critical<b>{sev.crit}</b></div>
                        </div>
                    </div>
                </Card>
            </div>

            <Card title="Error categories" sub="MQM dimensions, by severity"
                legend={SEV.map((s) => ({ label: s.name, color: s.color }))}>
                <div className="rpt-pad">
                    <HBars rows={D.MQM} segs={SEV} unit=" issues" />
                </div>
            </Card>

            <div className="rpt-row b">
                <Card title="Quality by language × category" sub="errors / 1,000 words">
                    <div className="rpt-pad">
                        {D.QUAL_HEAT.rows.length === 0 ? (
                            <div className="rpt-empty">No analyzed segments yet.</div>
                        ) : (
                            <Heat cols={D.QUAL_HEAT.cols} rows={D.QUAL_HEAT.rows} hue="accent" fmt={(v) => v.toFixed(1)} />
                        )}
                    </div>
                </Card>
                <Card title="LQA by language" sub="score · first-pass">
                    <div className="rpt-tbl lang">
                        <div className="rpt-tr head"><span>Language</span><span className="grow2">Score</span><span className="num">LQA</span><span className="num">Err/1k</span></div>
                        {langs.length === 0 ? (
                            <div className="rpt-empty">No reviewed languages yet.</div>
                        ) : langs.map(([code, name, , qual, , mqm]) => (
                            <div className="rpt-tr" key={code}>
                                <span className="who"><span className="rpt-code">{code}</span>{name}</span>
                                <span className="grow2"><Meter value={qual - 90} max={9} color={qual >= 96 ? 'var(--green)' : qual >= 94 ? 'var(--yellow)' : '#c8553a'} /></span>
                                <span className="num b">{qual}</span>
                                <span className="num">{(mqm || 0).toFixed(1)}</span>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>

            <Card title="Recent LQA reviews" sub="latest sampled reviews" right={<span className="rpt-link">Open all reviews {ICN.arrow}</span>}>
                <div className="rpt-tbl">
                    <div className="rpt-tr head q6"><span>Project</span><span>Lang</span><span className="num">Score</span><span>Result</span><span>Reviewer</span><span className="num">When</span></div>
                    {D.LQA_REVIEWS.length === 0 ? (
                        <div className="rpt-empty">No LQA reviews recorded yet.</div>
                    ) : D.LQA_REVIEWS.map(([proj, lang, score, status, reviewer, when], i) => {
                        const st = LQA_STATUS[status] || LQA_STATUS.rework;
                        return (
                            <div className="rpt-tr q6 link" key={i}>
                                <span className="who file">{proj}</span>
                                <span><span className="rpt-code">{lang}</span></span>
                                <span className="num b">{score}</span>
                                <span><span className={`rpt-badge ${st.cls}`}>{st.label}</span></span>
                                <span className="muted">{reviewer}</span>
                                <span className="num muted">{when}</span>
                            </div>
                        );
                    })}
                </div>
            </Card>
        </>
    );
};

export default QualityTab;
